import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Gavel,
  MessageSquareWarning,
  RefreshCw,
  Scale,
  ShieldAlert,
  UserRound
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Textarea } from '../components/ui/textarea'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/ui/toast'
import {
  AgeIndicator,
  DECISION_OPTIONS,
  DecisionToneIcon,
  InitialsAvatar,
  InterimActionBadge,
  QueueBadge,
  ReasonBadge,
  STRIKE_REQUIRED_REASONS,
  StatusBadge,
  TARGET_LABELS,
  TrustTierBadge,
  caseRef,
  decisionLabel,
  formatDateTime
} from '../components/moderation/moderationUi'
import {
  assignModerationCase,
  getModerationCaseDetail,
  getModerationStaff,
  resolveModerationCase,
  type ModerationCaseDetail,
  type ModerationDecision,
  type ModerationStaffMember
} from '../lib/api/moderation'

const formatAccountAge = (value?: string | null) => {
  if (!value) return 'Unknown age'
  const days = Math.floor((Date.now() - new Date(value).getTime()) / 864e5)
  if (days < 1) return 'Joined today'
  if (days < 30) return `Joined ${days}d ago`
  if (days < 365) return `Joined ${Math.floor(days / 30)}mo ago`
  return `Joined ${Math.floor(days / 365)}y ago`
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</div>
}

export const ModerationCaseDetailPage = () => {
  const { caseId } = useParams<{ caseId: string }>()
  const { user } = useAuth()
  const { addToast } = useToast()
  const navigate = useNavigate()
  const canResolve = user?.role === 'Admin' || user?.role === 'SupportAdmin'

  const [detail, setDetail] = useState<ModerationCaseDetail | null>(null)
  const [staff, setStaff] = useState<ModerationStaffMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [assigning, setAssigning] = useState(false)
  const [selectedDecision, setSelectedDecision] = useState<ModerationDecision | null>(null)
  const [decisionNotes, setDecisionNotes] = useState('')
  const [notifyOwner, setNotifyOwner] = useState(true)
  const [notifyReporters, setNotifyReporters] = useState(true)
  const [resolving, setResolving] = useState(false)

  const loadDetail = useCallback(async () => {
    if (!caseId) return
    setLoading(true)
    setError(null)
    try {
      const data = await getModerationCaseDetail(caseId)
      setDetail(data)
    } catch (err) {
      console.error('Failed to load moderation case', err)
      setError('Unable to load this moderation case.')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  useEffect(() => {
    getModerationStaff()
      .then(setStaff)
      .catch((err) => console.error('Failed to load moderation staff', err))
  }, [])

  const moderationCase = detail?.case ?? null
  const strikeRequired = moderationCase ? STRIKE_REQUIRED_REASONS.includes(moderationCase.highestReasonCode) : false

  const decisionInvalid = useMemo(() => {
    if (!strikeRequired) return () => false
    return (decision: ModerationDecision) =>
      decision !== 'DISMISS' && !DECISION_OPTIONS.find((option) => option.value === decision)?.appliesStrike
  }, [strikeRequired])

  const handleAssign = async (assigneeUserId: string | null) => {
    if (!moderationCase || assigning) return
    setAssigning(true)
    try {
      const updated = await assignModerationCase(moderationCase.id, assigneeUserId)
      setDetail((current) => (current ? { ...current, case: { ...current.case, ...updated } } : current))
    } catch (err) {
      console.error('Failed to assign moderation case', err)
      addToast({ title: 'Assignment failed', description: 'Unable to update the case assignment.', variant: 'error' })
    } finally {
      setAssigning(false)
    }
  }

  const handleResolve = async () => {
    if (!moderationCase || !selectedDecision || resolving || !canResolve) return
    setResolving(true)
    try {
      await resolveModerationCase(moderationCase.id, {
        decision: selectedDecision,
        notes: decisionNotes.trim() || null,
        notifyOwner,
        notifyReporters
      })
      const notified = [
        notifyOwner ? 'author' : null,
        notifyReporters && (detail?.reports.length ?? 0) > 0 ? 'reporters' : null
      ].filter(Boolean)
      addToast({
        title: `Applied: ${decisionLabel(selectedDecision)}`,
        description: notified.length > 0 ? `In-app notifications sent to the ${notified.join(' and ')}.` : undefined,
        variant: 'success'
      })
      setSelectedDecision(null)
      setDecisionNotes('')
      await loadDetail()
    } catch (err) {
      console.error('Failed to resolve moderation case', err)
      addToast({ title: 'Decision failed', description: 'Unable to apply that moderation decision.', variant: 'error' })
    } finally {
      setResolving(false)
    }
  }

  if (loading) {
    return <p className="p-8 text-center text-sm text-muted-foreground">Loading case…</p>
  }

  if (error || !detail || !moderationCase) {
    return (
      <div className="space-y-4 p-8 text-center">
        <p className="text-sm text-red-700">{error ?? 'Moderation case not found.'}</p>
        <Button variant="outline" onClick={() => navigate('/support/moderation')}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to queue
        </Button>
      </div>
    )
  }

  const { target, targetOwner, reports } = detail
  const assignedToMe = moderationCase.assignedToUserId === user?.id
  const isOpen = moderationCase.status === 'Open'

  return (
    <div className="space-y-4 pb-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to="/support/moderation"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Moderation queue
        </Link>
        <Button variant="outline" size="sm" onClick={() => void loadDetail()}>
          <RefreshCw className="mr-1 h-3 w-3" />
          Refresh
        </Button>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-elevation-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-lg font-bold tracking-tight">{caseRef(moderationCase.id)}</h1>
              <QueueBadge queue={moderationCase.queue} />
              <StatusBadge status={moderationCase.status} />
              <ReasonBadge reason={moderationCase.highestReasonCode} />
              {moderationCase.suspectedBrigade && (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-800">
                  Possible brigade
                </span>
              )}
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {TARGET_LABELS[moderationCase.targetType]} case · opened {formatDateTime(moderationCase.createdAt)} ·
              <AgeIndicator createdAt={moderationCase.createdAt} status={moderationCase.status} />
            </p>
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div>
              {moderationCase.reportCount24h} report{moderationCase.reportCount24h === 1 ? '' : 's'} in 24h · weighted{' '}
              {moderationCase.weightedReports24h.toFixed(1)}
            </div>
            <div className="mt-1 flex justify-end gap-1.5">
              <InterimActionBadge action={target.interimAction} />
              {target.threadLockedAt && (
                <span
                  className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold leading-4 text-indigo-800"
                  title={`Thread locked ${formatDateTime(target.threadLockedAt)}`}
                >
                  Comments locked
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {moderationCase.suspectedBrigade && isOpen && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Many reports arrived from very new accounts in a short window. Weigh reporter history carefully before applying a
          severe action.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <div className="min-w-0 space-y-4">
          <Card className="rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldAlert className="h-4 w-4 text-red-600" />
                Reported content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {!target.exists ? (
                <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                  The reported {TARGET_LABELS[moderationCase.targetType].toLowerCase()} no longer exists. It may have been
                  deleted by its author.
                </p>
              ) : (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <InitialsAvatar name={target.authorUsername} imageUrl={target.authorProfileImageUrl} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">@{target.authorUsername ?? 'unknown'}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(target.createdAt)}
                        {typeof target.likeCount === 'number' ? ` · ${target.likeCount} likes` : ''}
                        {typeof target.commentCount === 'number' ? ` · ${target.commentCount} comments` : ''}
                      </p>
                    </div>
                  </div>
                  {target.activityTitle && <p className="mb-1 text-sm font-semibold">{target.activityTitle}</p>}
                  {target.content ? (
                    <p className="whitespace-pre-wrap text-sm text-foreground">{target.content}</p>
                  ) : (
                    <p className="text-sm italic text-muted-foreground">No text content.</p>
                  )}
                  {target.imageUrls.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {target.imageUrls.map((url) => (
                        <a key={url} href={url} target="_blank" rel="noreferrer" className="block">
                          <img
                            src={url}
                            alt="Reported attachment"
                            className="h-24 w-24 rounded-lg border border-border object-cover transition hover:opacity-80"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {target.parentPostId && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Comment on post <span className="break-all font-mono">{target.parentPostId}</span>
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {moderationCase.appealedAt && (
            <Card className="rounded-xl border-indigo-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base text-indigo-800">
                  <Scale className="h-4 w-4" />
                  Appeal from the author
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Appealed {formatDateTime(moderationCase.appealedAt)} · original decision: {decisionLabel(moderationCase.decision)}
                </p>
                {moderationCase.appealDetails ? (
                  <blockquote className="border-l-2 border-indigo-300 pl-3 text-sm italic text-foreground">
                    “{moderationCase.appealDetails}”
                  </blockquote>
                ) : (
                  <p className="text-sm italic text-muted-foreground">No appeal details were provided.</p>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquareWarning className="h-4 w-4 text-amber-600" />
                Reports ({reports.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {reports.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No user reports on file — this case was opened automatically (for example by spam detection).
                </p>
              )}
              {reports.map((report) => {
                const reviewed = report.reporterUpheldReportCount + report.reporterOverturnedReportCount
                return (
                  <div key={report.id} className="rounded-lg border border-border/60 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <InitialsAvatar name={report.reporterUsername ?? report.reporterId} imageUrl={report.reporterProfileImageUrl} size="h-7 w-7" />
                        <div className="min-w-0">
                          <Link
                            to={`/users/mobile/${encodeURIComponent(report.reporterId)}`}
                            className="block truncate text-sm font-semibold text-primary hover:underline"
                          >
                            @{report.reporterUsername ?? report.reporterId}
                          </Link>
                          <p className="text-[11px] text-muted-foreground">
                            {formatAccountAge(report.reporterAccountCreatedAt)}
                            {reviewed > 0
                              ? ` · past reports: ${report.reporterUpheldReportCount} upheld / ${report.reporterOverturnedReportCount} overturned`
                              : ' · no reviewed reports yet'}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <ReasonBadge reason={report.reasonCode} />
                        <TrustTierBadge tier={report.trustTier} />
                        <span className="font-mono text-[11px] text-muted-foreground" title="Report weight from reporter trust">
                          ×{report.weight.toFixed(1)}
                        </span>
                      </div>
                    </div>
                    {report.details ? (
                      <blockquote className="mt-2 border-l-2 border-amber-300 pl-3 text-sm text-foreground">
                        “{report.details}”
                      </blockquote>
                    ) : (
                      <p className="mt-2 text-xs italic text-muted-foreground">No additional details from the reporter.</p>
                    )}
                    <p className="mt-2 text-[11px] text-muted-foreground">Reported {formatDateTime(report.lastReportedAt)}</p>
                  </div>
                )
              })}
            </CardContent>
          </Card>

          {isOpen ? (
            <Card className="rounded-xl border-primary/30">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Gavel className="h-4 w-4" />
                    Decision
                  </CardTitle>
                  {!canResolve && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                      View only — a Support Admin must apply decisions
                    </span>
                  )}
                </div>
                {strikeRequired && (
                  <p className="text-xs text-amber-700">
                    This category ({moderationCase.highestReasonCode}) requires at least “Remove + strike” unless dismissed.
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {DECISION_OPTIONS.map((option) => {
                    const invalid = decisionInvalid(option.value)
                    return (
                      <button
                        key={option.value}
                        type="button"
                        disabled={!canResolve || invalid}
                        title={invalid ? 'Not allowed for this report category — a strike is required.' : undefined}
                        onClick={() => setSelectedDecision(option.value)}
                        className={`rounded-lg border p-3 text-left transition disabled:cursor-not-allowed disabled:opacity-45 ${
                          selectedDecision === option.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                        }`}
                      >
                        <div className="mb-1 flex items-center gap-2">
                          <DecisionToneIcon tone={option.tone} />
                          <span className="text-sm font-semibold">{option.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      </button>
                    )
                  })}
                </div>

                <div className="space-y-2">
                  <label htmlFor="moderation-notes" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Moderator notes (internal audit trail)
                  </label>
                  <Textarea
                    id="moderation-notes"
                    value={decisionNotes}
                    onChange={(event) => setDecisionNotes(event.target.value)}
                    placeholder="Summarize what you saw and why you chose this outcome…"
                    disabled={!canResolve}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/20 p-3">
                  <div className="space-y-1.5 text-sm">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={notifyOwner}
                        onChange={(event) => setNotifyOwner(event.target.checked)}
                        disabled={!canResolve}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                      />
                      Notify the author of the outcome in-app
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={notifyReporters}
                        onChange={(event) => setNotifyReporters(event.target.checked)}
                        disabled={!canResolve}
                        className="h-4 w-4 accent-[hsl(var(--primary))]"
                      />
                      Notify {reports.length === 1 ? 'the reporter' : `all ${reports.length} reporters`} in-app
                    </label>
                    <p className="text-[11px] text-muted-foreground">
                      The author is not notified when reports on unhidden content are dismissed.
                    </p>
                  </div>
                  <Button onClick={() => void handleResolve()} disabled={!canResolve || !selectedDecision || resolving}>
                    {resolving
                      ? 'Applying…'
                      : selectedDecision
                        ? `Apply ${decisionLabel(selectedDecision)}`
                        : 'Select a decision'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Resolution
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm">
                  <span className="font-semibold">{decisionLabel(moderationCase.decision)}</span>
                  <span className="text-muted-foreground">
                    {' '}· {formatDateTime(moderationCase.resolvedAt)}
                    {moderationCase.resolvedByUserId ? ` · by ${staff.find((member) => member.userId === moderationCase.resolvedByUserId)?.name ?? 'staff'}` : ''}
                  </span>
                </p>
                {moderationCase.decisionNotes && (
                  <blockquote className="border-l-2 border-border pl-3 text-sm text-muted-foreground">
                    {moderationCase.decisionNotes}
                  </blockquote>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Assignment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {moderationCase.assignedToUserId ? (
                <div className="flex items-center gap-2">
                  <InitialsAvatar name={moderationCase.assignedToName} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{assignedToMe ? 'You' : moderationCase.assignedToName}</p>
                    <p className="text-xs text-muted-foreground">since {formatDateTime(moderationCase.assignedAt)}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nobody is working this case yet.</p>
              )}

              {isOpen && (
                <div className="space-y-2">
                  {!assignedToMe && (
                    <Button className="w-full" variant="outline" size="sm" disabled={assigning} onClick={() => void handleAssign(user?.id ?? null)}>
                      {moderationCase.assignedToUserId ? 'Take over this case' : 'Claim this case'}
                    </Button>
                  )}
                  {staff.length > 0 && (
                    <select
                      value=""
                      disabled={assigning}
                      onChange={(event) => {
                        if (event.target.value) void handleAssign(event.target.value)
                      }}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                    >
                      <option value="">Assign to a teammate…</option>
                      {staff
                        .filter((member) => member.userId !== moderationCase.assignedToUserId)
                        .map((member) => (
                          <option key={member.userId} value={member.userId}>
                            {member.name} ({member.role})
                          </option>
                        ))}
                    </select>
                  )}
                  {moderationCase.assignedToUserId && (
                    <Button className="w-full" variant="ghost" size="sm" disabled={assigning} onClick={() => void handleAssign(null)}>
                      Clear assignment
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {targetOwner && (
            <Card className="rounded-xl">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <UserRound className="h-4 w-4" />
                  Content author
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <InitialsAvatar name={targetOwner.username ?? targetOwner.fullName} imageUrl={targetOwner.profileImageUrl} />
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/users/mobile/${encodeURIComponent(targetOwner.userId)}`}
                      className="flex items-center gap-1 truncate text-sm font-semibold text-primary hover:underline"
                    >
                      @{targetOwner.username ?? targetOwner.fullName ?? targetOwner.userId}
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </Link>
                    <p className="text-xs text-muted-foreground">{formatAccountAge(targetOwner.accountCreatedAt)}</p>
                  </div>
                </div>

                <dl className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Strikes (90d)</dt>
                    <dd className={`font-mono font-bold ${targetOwner.strikeCount90d > 0 ? 'text-red-700' : ''}`}>
                      {targetOwner.strikeCount90d}
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Prior cases against them</dt>
                    <dd className="font-mono">
                      <span className={targetOwner.priorCasesUpheld > 0 ? 'font-bold text-red-700' : ''}>{targetOwner.priorCasesUpheld} upheld</span>
                      <span className="text-muted-foreground"> / {targetOwner.priorCasesDismissed} dismissed</span>
                    </dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-muted-foreground">Reports they filed</dt>
                    <dd className="font-mono text-muted-foreground">
                      {targetOwner.upheldReportCount} upheld / {targetOwner.overturnedReportCount} overturned
                    </dd>
                  </div>
                </dl>

                {(targetOwner.bannedAt || targetOwner.suspendedUntil || targetOwner.postingMutedUntil) && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-800">
                    <SectionLabel>Active restrictions</SectionLabel>
                    {targetOwner.bannedAt && <p>Banned since {formatDateTime(targetOwner.bannedAt)}</p>}
                    {targetOwner.suspendedUntil && new Date(targetOwner.suspendedUntil) > new Date() && (
                      <p>Suspended until {formatDateTime(targetOwner.suspendedUntil)}</p>
                    )}
                    {targetOwner.postingMutedUntil && new Date(targetOwner.postingMutedUntil) > new Date() && (
                      <p>Posting muted until {formatDateTime(targetOwner.postingMutedUntil)}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Case log</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-xs">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Case ID</dt>
                  <dd className="break-all font-mono">{moderationCase.id}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Target ID</dt>
                  <dd className="break-all font-mono">{moderationCase.targetId}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Opened</dt>
                  <dd>{formatDateTime(moderationCase.createdAt)}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Last update</dt>
                  <dd>{formatDateTime(moderationCase.updatedAt)}</dd>
                </div>
                {moderationCase.priorityAlertedAt && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Priority alert</dt>
                    <dd>{formatDateTime(moderationCase.priorityAlertedAt)}</dd>
                  </div>
                )}
                {moderationCase.resolvedAt && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Resolved</dt>
                    <dd>{formatDateTime(moderationCase.resolvedAt)}</dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
